import { fetchAuthSession, fetchUserAttributes, getCurrentUser, signIn, signOut } from 'aws-amplify/auth';

const createTokenAdapter = (token) => ({
  payload: token?.payload || {},
  jwtToken: token?.toString() || '',
  getJwtToken: () => token?.toString() || '',
});

export const adaptAuthSession = (session) => {
  const idToken = createTokenAdapter(session?.tokens?.idToken);
  const accessToken = createTokenAdapter(session?.tokens?.accessToken);

  return {
    ...session,
    idToken,
    accessToken,
    getIdToken: () => idToken,
    getAccessToken: () => accessToken,
    isValid: () => {
      const expirySeconds = idToken.payload?.exp;
      return Boolean(session?.tokens?.idToken && (!expirySeconds || expirySeconds * 1000 > Date.now()));
    },
  };
};

export const getCurrentSession = async (options) => adaptAuthSession(await fetchAuthSession(options));

export const getCurrentAuthenticatedUser = async () => {
  const [user, attributes, session] = await Promise.all([
    getCurrentUser(),
    fetchUserAttributes().catch(() => ({})),
    getCurrentSession().catch(() => null),
  ]);

  return {
    username: user.username,
    userId: user.userId,
    attributes: {
      ...attributes,
      sub: attributes.sub || user.userId,
    },
    signInUserSession: session
      ? {
          idToken: session.getIdToken(),
          accessToken: session.getAccessToken(),
          getIdToken: () => session.getIdToken(),
          getAccessToken: () => session.getAccessToken(),
        }
      : null,
  };
};

const isAlreadySignedInError = (error) => {
  const message = `${error?.name || ''} ${error?.message || ''}`.toLowerCase();
  return message.includes('already') && message.includes('sign');
};

const buildAuthenticatedUser = async (sessionOptions) => {
  const [user, attributes, session] = await Promise.all([
    getCurrentUser(),
    fetchUserAttributes().catch(() => ({})),
    getCurrentSession(sessionOptions),
  ]);

  return {
    username: user.username,
    userId: user.userId,
    attributes: {
      ...attributes,
      sub: attributes.sub || user.userId,
    },
    signInUserSession: {
      idToken: session.getIdToken(),
      accessToken: session.getAccessToken(),
      getIdToken: () => session.getIdToken(),
      getAccessToken: () => session.getAccessToken(),
    },
  };
};

export const signInAndGetUser = async (username, password) => {
  let result;
  try {
    result = await signIn({ username, password });
  } catch (error) {
    if (!isAlreadySignedInError(error)) {
      throw error;
    }

    await signOut();
    result = await signIn({ username, password });
  }

  if (result?.isSignedIn === false) {
    throw new Error(`Sign in requires additional step: ${result.nextStep?.signInStep || 'unknown'}`);
  }

  return buildAuthenticatedUser({ forceRefresh: true });
};
